import React, { useMemo } from 'react'
import { localizedText } from '../utils/localized'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AiOutlineSearch } from 'react-icons/ai'
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md'
import { useCurrent, useFetch } from '../hooks'
import { AuthAvatar, CardHeaderWithActions, MwButton, MwSelector, MwSpinnerButton, NoDataFound, TruncatedText } from '../components/ui'
import { getCatalogLabel, normalizeSupportSettings } from '../constants/ticket'
import { formatAssigneeNames, getTicketAssignees } from '../utils/ticketAssignees'
import { formatDate, getScopedTenants, getTenantLabel, isTruthyFlag } from '../utils/projectHelpers'
import ProjectStatusBadge from '../components/projects/ProjectStatusBadge'
import CollapsibleFilters from '../components/ui/CollapsibleFilters'

const PAGE_SIZE = 12;
const FILTER_KEYS = ['tenantId', 'section', 'product', 'assignee', 'q'];

const ProjectsManage = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, currentLangId } = useCurrent();
    const [t, i18n] = useTranslation('global');
    const isAr = currentLangId === 'ar';
    const isRtl = i18n.dir() === 'rtl';
    const canFilterByAssignee =
        user?.role === 'admin' ||
        (isTruthyFlag(user?.supportAdminEnabled) && isTruthyFlag(user?.supportSuperAdminEnabled));

    const projectsUrl = useMemo(() => {
        const apiParams = new URLSearchParams();
        apiParams.set('limit', String(PAGE_SIZE));
        apiParams.set('page', searchParams.get('page') || '1');
        FILTER_KEYS.forEach((key) => {
            const value = String(searchParams.get(key) || '').trim();
            if (value && value !== 'all') apiParams.set(key, value);
        });
        return `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/admin?${apiParams.toString()}`;
    }, [searchParams]);

    const { data: projectsResult, loading } = useFetch(projectsUrl);
    const { data: supportSettingsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`);
    const { data: tenantsResult } = useFetch(`${process.env.REACT_APP_Auth_API_BASE_URL}/tenants/getAll`);
    const { data: adminsResult } = useFetch(`${process.env.REACT_APP_Auth_API_BASE_URL}/users/support-admins`);

    const supportSettings = useMemo(() => normalizeSupportSettings(supportSettingsResult), [supportSettingsResult]);
    const tenants = useMemo(() => getScopedTenants(tenantsResult?.data, user), [tenantsResult, user]);
    const admins = useMemo(() => (Array.isArray(adminsResult?.data) ? adminsResult.data : []), [adminsResult]);
    const rows = Array.isArray(projectsResult?.data) ? projectsResult.data : [];
    const pagination = projectsResult?.pagination || null;
    const totalPages = Math.max(1, Number(pagination?.totalPages || 1));
    const totalRows = Math.max(0, Number(pagination?.total || 0));
    const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(searchParams.toString());
        const resolved = String(value || '').trim();
        if (!resolved || resolved === 'all') next.delete(key);
        else next.set(key, resolved);
        if (key !== 'page') next.delete('page');
        setSearchParams(next, { replace: true });
    };

    const allOption = (label) => ({ label, value: 'all', lang: currentLangId });
    const tenantOptions = useMemo(() => [
        allOption(isAr ? 'كل العملاء' : 'All Customers'),
        ...tenants.map((tenant) => ({ label: getTenantLabel(tenant), value: tenant._id, lang: currentLangId }))
    ], [currentLangId, tenants]); // eslint-disable-line react-hooks/exhaustive-deps
    const sectionOptions = useMemo(() => [
        allOption(t('support.tickets.allSections')),
        ...(supportSettings?.sections || []).filter((item) => item?.isActive !== false)
            .map((item) => ({ label: isAr ? item.labelAr : item.labelEn, value: item.key, lang: currentLangId }))
    ], [currentLangId, supportSettings?.sections, t]); // eslint-disable-line react-hooks/exhaustive-deps
    const productOptions = useMemo(() => [
        allOption(t('support.tickets.allProducts')),
        ...(supportSettings?.products || []).filter((item) => item?.isActive !== false)
            .map((item) => ({ label: isAr ? item.labelAr : item.labelEn, value: item.key, lang: currentLangId }))
    ], [currentLangId, supportSettings?.products, t]); // eslint-disable-line react-hooks/exhaustive-deps
    const assigneeOptions = useMemo(() => [
        allOption(isAr ? 'الكل' : 'All'),
        { label: isAr ? 'غير معيّنة' : 'Unassigned', value: 'unassigned', lang: currentLangId },
        ...admins.map((admin) => ({
            label: admin?.name || admin?.username || admin?._id || '',
            value: admin?._id,
            lang: currentLangId,
            subLabel: admin?.jobTitle || '',
            avatar: admin?.userPic || admin?.avatar || admin?.image || admin?.profileImage || admin?.photo || ''
        }))
    ], [admins, currentLangId]); // eslint-disable-line react-hooks/exhaustive-deps
    const selectedOf = (options, key) => options.find((item) => item.value === (searchParams.get(key) || 'all')) || options[0];

    const filterFields = [
        { key: 'tenantId', label: isAr ? 'فلترة حسب العميل' : 'Filter by Customer', options: tenantOptions, style: { minWidth: 200, flex: '1 1 200px' } },
        { key: 'product', label: t('support.tickets.product'), options: productOptions, style: { minWidth: 200, flex: '1 1 200px' } },
        { key: 'section', label: t('support.tickets.section'), options: sectionOptions, style: { minWidth: 200, flex: '1 1 200px' } },
        ...(canFilterByAssignee
            ? [{ key: 'assignee', label: isAr ? 'فلترة حسب المعيّن' : 'Filter by Assignee', options: assigneeOptions, style: { minWidth: 220, flex: '1 1 220px' }, avatar: true }]
            : []),
    ];

    // مسح كل الفلاتر مرة واحدة (البحث بيفضل زي ما هو).
    const clearFilters = () => {
        const next = new URLSearchParams(searchParams.toString());
        filterFields.forEach((field) => next.delete(field.key));
        next.delete('page');
        setSearchParams(next, { replace: true });
    };

    const cols = [
        { key: isAr ? 'رقم المشروع' : 'Project #', width: 100, align: 'center' },
        { key: isAr ? 'عنوان المشروع / العميل' : 'Project title / Customer', flex: 1, align: isRtl ? 'right' : 'left' },
        { key: isAr ? 'معينة لـ' : 'Assigned To', width: 170, align: isRtl ? 'right' : 'left' },
        { key: `${t('support.tickets.section')} / ${t('support.tickets.product')}`, width: 155, align: isRtl ? 'right' : 'left' },
        { key: isAr ? 'البداية / المتوقع' : 'Start / Expected', width: 130, align: 'center' },
        { key: isAr ? 'الحالة' : 'Status', width: 100, align: 'center' },
        { key: isAr ? 'التذاكر' : 'Tickets', width: 80, align: 'center' },
    ];
    const gridTemplate = cols.map((col) => (col.flex ? '1fr' : `${col.width}px`)).join(' ');
    const adminById = (id) => admins.find((admin) => String(admin?._id) === String(id));

    return (
        <div className="admin-page">
            <CardHeaderWithActions title={isAr ? 'المشاريع' : 'Projects'}>
                <MwButton type="saveBtn" onClick={() => navigate(`/${params.tenant}/projects/add`)}>
                    {isAr ? 'إنشاء مشروع' : 'Create Project'}
                </MwButton>
            </CardHeaderWithActions>
            <div className="admin-panel p-3 md:p-4">
                <div style={{ fontFamily: "'Cairo', 'Segoe UI', sans-serif", direction: isRtl ? 'rtl' : 'ltr', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* نفس فلاتر التذاكر: بتتلم في لوحة بأنيميشن، والفلاتر الشغالة بتظهر كـ chips، والبحث دايماً ظاهر. */}
                    <CollapsibleFilters
                        lang={currentLangId}
                        storageKey="support.projectFilters.open"
                        chips={filterFields
                            .filter((field) => (searchParams.get(field.key) || 'all') !== 'all')
                            .map((field) => ({
                                key: field.key,
                                label: field.label,
                                value: selectedOf(field.options, field.key)?.label || searchParams.get(field.key),
                                onRemove: () => updateParam(field.key, 'all'),
                            }))}
                        onClearAll={clearFilters}
                        footer={(
                            <div style={{ position: 'relative' }}>
                                <AiOutlineSearch size={18} style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', [isRtl ? 'right' : 'left']: 12, color: '#94a3b8' }} />
                                <input
                                    type="text"
                                    defaultValue={searchParams.get('q') || ''}
                                    onChange={(e) => updateParam('q', e.target.value)}
                                    placeholder={isAr ? 'ابحث برقم المشروع أو عنوانه أو رقم العقد...' : 'Search by project number, title or contract...'}
                                    style={{ width: '100%', height: 34, padding: isRtl ? '0 40px 0 12px' : '0 12px 0 40px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', background: '#f8fafc' }}
                                    onFocus={(e) => { e.target.style.borderColor = '#6366f1'; }}
                                    onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
                                />
                            </div>
                        )}
                    >
                        {filterFields.map((field) => (
                            <div key={field.key} style={field.style}>
                                <MwSelector
                                    label={field.label}
                                    initalValue={field.options[0].label}
                                    dataType="labelValue"
                                    _data={field.options}
                                    selectedItem={selectedOf(field.options, field.key)}
                                    setSelectedItem={(item) => updateParam(field.key, item?.value)}
                                    withAddNew={false}
                                    showAvatarFallback={Boolean(field.avatar)}
                                />
                            </div>
                        ))}
                    </CollapsibleFilters>

                    <div style={{ fontSize: 12, fontWeight: 800, color: '#475569' }}>
                        {isAr ? `إجمالي المشاريع: ${totalRows}` : `Total projects: ${totalRows}`}
                    </div>

                    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', minHeight: 420 }}>
                        <div className="table-scroll" style={{ overflowX: 'auto' }}>
                            <div style={{ minWidth: 900 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: gridTemplate, padding: '11px 16px', background: '#f8fafc', borderBottom: '2px solid #e2e8f0', gap: 8 }}>
                                    {cols.map((col) => (
                                        <div key={col.key} style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, textAlign: col.align }}>{col.key}</div>
                                    ))}
                                </div>
                                {loading ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 24px' }}>
                                        <MwSpinnerButton withLabel={false} isFullCenter />
                                    </div>
                                ) : rows.length ? rows.map((item) => {
                                    const assignees = getTicketAssignees(item).map((entry) => {
                                        const admin = adminById(entry.userId);
                                        return {
                                            userId: entry.userId,
                                            name: entry.name || admin?.name || entry.userId,
                                            avatar: admin?.userPic || admin?.avatar || admin?.image || ''
                                        };
                                    });
                                    const section = item.sectionId ? getCatalogLabel(supportSettings?.sections, item.sectionId, currentLangId) : '-';
                                    const product = item.ProductId ? getCatalogLabel(supportSettings?.products, item.ProductId, currentLangId) : '-';
                                    return (
                                        <div
                                            key={item._id}
                                            onClick={() => navigate(`/${params.tenant}/projects/show/${item._id}`)}
                                            style={{ display: 'grid', gridTemplateColumns: gridTemplate, gap: 8, alignItems: 'center', padding: '14px 16px', fontSize: 12, color: '#475569', background: '#fff', borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                                        >
                                            <div style={{ textAlign: 'center', fontWeight: 700, color: '#0f172a' }}>{item.projectNumber ?? '-'}</div>
                                            <div style={{ minWidth: 0 }}>
                                                <TruncatedText text={localizedText(item, 'title', currentLangId)} style={{ fontWeight: 700, color: '#0f172a' }} />
                                                <TruncatedText text={item.tenantName || item.customerName || '-'} style={{ marginTop: 2 }} />
                                                {item.contractNumber && (
                                                    <div style={{ marginTop: 3, fontSize: 10.5, fontWeight: 700, color: '#64748b' }}>
                                                        {isAr ? 'رقم العقد:' : 'Contract #:'} <span dir="ltr">{item.contractNumber}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, fontSize: 11, color: '#6366f1', fontWeight: 600 }}>
                                                {assignees.length ? (
                                                    <>
                                                        <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                                                            {assignees.slice(0, 3).map((entry, index) => (
                                                                <AuthAvatar key={entry.userId} src={entry.avatar} alt={entry.name} size={22} style={{ border: '2px solid #fff', marginInlineStart: index === 0 ? 0 : -8 }} />
                                                            ))}
                                                        </span>
                                                        <TruncatedText as="span" text={formatAssigneeNames(assignees.map((entry) => entry.name), currentLangId)} />
                                                    </>
                                                ) : <span style={{ color: '#94a3b8' }}>-</span>}
                                            </div>
                                            <div style={{ lineHeight: 1.35 }}>
                                                <div style={{ fontWeight: 500, color: '#0f172a' }}>{section}</div>
                                                <div style={{ marginTop: 2, fontSize: 11, color: '#64748b' }}>{product}</div>
                                            </div>
                                            <div style={{ textAlign: 'center', lineHeight: 1.35 }}>
                                                <div style={{ fontWeight: 500, color: '#0f172a' }}>{formatDate(item.startAt)}</div>
                                                <div style={{ marginTop: 2, fontSize: 11, fontWeight: 800, color: '#16a34a' }}>{formatDate(item.expectedAt)}</div>
                                            </div>
                                            <div style={{ textAlign: 'center' }}>
                                                <ProjectStatusBadge status={item.status} lang={currentLangId} size="sm" />
                                            </div>
                                            <div style={{ textAlign: 'center' }}>
                                                <span style={{ display: 'inline-flex', minWidth: 28, justifyContent: 'center', padding: '3px 8px', borderRadius: 999, background: '#eef2ff', color: '#4f46e5', fontWeight: 900 }}>
                                                    {item.ticketsCount || 0}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 24px' }}>
                                        <NoDataFound msg={isAr ? 'لا توجد مشاريع' : 'No projects found'} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {totalRows > PAGE_SIZE && (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 'auto', padding: 16, borderTop: '1px solid #e2e8f0', background: '#f8fafc', flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    disabled={currentPage <= 1}
                                    onClick={() => updateParam('page', currentPage - 1 > 1 ? currentPage - 1 : '')}
                                    style={{ padding: '6px 12px', borderRadius: 8, background: '#e2e8f0', color: '#334155', border: 'none', cursor: 'pointer', opacity: currentPage <= 1 ? 0.5 : 1 }}
                                >
                                    {isRtl ? <MdKeyboardArrowRight size={20} /> : <MdKeyboardArrowLeft size={20} />}
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter((number) => totalPages <= 5 || number === 1 || number === totalPages || Math.abs(currentPage - number) < 3)
                                    .map((number) => (
                                        <button
                                            key={number}
                                            type="button"
                                            onClick={() => updateParam('page', number > 1 ? number : '')}
                                            style={{ padding: '6px 12px', borderRadius: 8, background: currentPage === number ? '#4f46e5' : '#e2e8f0', color: currentPage === number ? '#fff' : '#334155', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
                                        >
                                            {number}
                                        </button>
                                    ))}
                                <button
                                    type="button"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => updateParam('page', Math.min(totalPages, currentPage + 1))}
                                    style={{ padding: '6px 12px', borderRadius: 8, background: '#e2e8f0', color: '#334155', border: 'none', cursor: 'pointer', opacity: currentPage >= totalPages ? 0.5 : 1 }}
                                >
                                    {isRtl ? <MdKeyboardArrowLeft size={20} /> : <MdKeyboardArrowRight size={20} />}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectsManage;
